import { WbHeaderBar, WbHeaderButton, WbHeaderLanguageMenu, WbHeaderMetaLink, WbIcon } from '@workbench/react';
import { useTranslation } from '../common/i18n/useTranslation';
import { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import LoginMenu from './LoginMenu';
import { SupportedLanguages } from '../common/i18n/SupportedLanguages';
import { useTranslationContext } from '../common/i18n/TranslationProvider';
import { makeStyles } from 'tss-react/mui';
import { Box, Theme, Badge } from '@mui/material';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { MediaQuery, MediaQueryChangeEvent, MediaQueryService } from '@workbench/core';
import { useContentRefContext } from './contexts/ContentRefProvider';
import { useWindowDimensions } from '../utils/customHooks';
import ChangeDevUserDialog from './ChangeDevUserDialog';
import { Routes } from '../routes/routes';
import { FooterTabNamesEnum, LandingPageMode } from '../utils/enums';
import { useLoadingContext } from './contexts/LoadingContext';
import { useClientCompanyRegistrationContext } from './client/companyRegistration/context/ClientCompanyRegistrationContext';
import { useLandingPageModeContext } from './contexts/LandingPageModeProvider';
import MBAppBarUserNavigation from './MBAppBarUserNavigation';
import { CompanyRegistrationDto } from '../api';

const myMediaQueryService = new MediaQueryService(window);

const useStyles = makeStyles()((theme: Theme) => ({
    languageContainer: {
        alignItems: 'center',
        borderRadius: theme.spacing(0.1),
        fontSize: theme.spacing(1.5),
        color: theme.palette.grey[200],
        display: 'flex',
        width: theme.spacing(3.5),
        cursor: 'pointer',
    },
    unselectedLanguage: {
        paddingLeft: theme.spacing(0.7),
        paddingRight: theme.spacing(0.7),
        '&:hover': {
            fontWeight: 'bold',
        },
    },
    selectedLanguage: {
        backgroundColor: theme.palette.grey[900],
        paddingLeft: theme.spacing(0.7),
        paddingRight: theme.spacing(0.7),
        fontWeight: 'bold',
    },
    header: {
        position: 'fixed',
        width: '100%',
        top: 0,
    },
    badge: {
        color: theme.palette.background.default,
        backgroundColor: theme.palette.info.main,
        borderRadius: '50%',
    },
    appBarLink: {
        cursor: 'pointer',
    },
    headerBackgroundColor: {
        backgroundColor: theme.palette.common.black,
    },
    marginRight: {
        marginRight: theme.spacing(1),
    },
}));

interface MBAppBarProps {
    setInitialFooterModalTab: (tabName: string) => void;
    showFooterModal: () => void;
}

export function MBAppBar(props: Readonly<MBAppBarProps>) {
    const appbarRef = useRef<HTMLWbHeaderBarElement>(null);
    const currentMediaQuery: MediaQuery = myMediaQueryService.getCurrentMediaQuery();
    const translate = useTranslation();
    const { langState, updateLangState } = useTranslationContext();
    const { setAppbarSize, appbarSize } = useContentRefContext();
    const { width, height } = useWindowDimensions();
    const [openChangeDevUserDialog, setOpenChangeDevUserDialog] = useState<boolean>(false);
    const navigateTo = useNavigate();
    const { loading } = useLoadingContext();
    const isAdminPage = useMatch(`/${Routes.admin}/*`);
    const { classes } = useStyles();
    const { mode, setMode } = useLandingPageModeContext();
    const { registration, userInfo, isAdminAndUser, isAdmin } = useClientCompanyRegistrationContext();
    const [fullAccess, setFullAccess] = useState<boolean>(false);

    useLayoutEffect(() => {
        if (appbarRef.current) {
            setAppbarSize(appbarRef.current.getBoundingClientRect().height);
        }
    }, [width, height, setAppbarSize]);

    const getHeaderSpacerSize = useCallback((mq: MediaQueryChangeEvent['current']): number => {
        if (appbarRef.current && appbarRef.current.getBoundingClientRect().height !== 0) {
            return appbarRef.current.getBoundingClientRect().height;
        }

        //Initially the WBHeader doesnt know how big it is (?), use same height as WBHeader uses if it is 0
        if (mq === 'mq1' || mq === 'mq2') {
            return 80;
        }
        //Default height for dummy header box
        return 96;
    }, []);

    useEffect(() => {
        if (appbarRef.current) {
            setAppbarSize(getHeaderSpacerSize(currentMediaQuery));
        }
    }, [
        appbarRef.current?.clientHeight,
        appbarRef.current?.offsetHeight,
        setAppbarSize,
        currentMediaQuery,
        getHeaderSpacerSize,
    ]);

    const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);

    const handleMenuClick = (event: React.MouseEvent<HTMLWbHeaderButtonElement, MouseEvent>) => {
        setAnchorElement(event.currentTarget);
    };

    const handleOpenChangeDevUserDialog = () => {
        setOpenChangeDevUserDialog(true);
    };

    const handleCloseChangeDevUserDialog = () => {
        setOpenChangeDevUserDialog(false);
    };
    const showFooterModal = (tabName: string) => {
        props.setInitialFooterModalTab(tabName);
        props.showFooterModal();
    };

    useEffect(() => {
        if (
            registration?.termsOfUseApproved === true &&
            registration?.registrationState === CompanyRegistrationDto.registrationState.APPROVED
        ) {
            setMode(LandingPageMode.APPROVED);
            setFullAccess(mode === LandingPageMode.APPROVED);
        }
    }, [mode, setMode, registration?.termsOfUseApproved]);

    const showUserAppBarNavigation = () => {
        return !isAdminPage && fullAccess && !loading && (isAdminAndUser || !isAdmin);
    };
    return (
        <header className={classes.headerBackgroundColor}>
            <WbHeaderBar
                star-href={isAdminPage ? `/${Routes.admin_dashboard}` : '/'}
                className={classes.header}
                ref={appbarRef}
            >
                {!loading && (
                    <>
                        <WbHeaderMetaLink
                            slot="desktop-meta"
                            onClick={() => {
                                showFooterModal(FooterTabNamesEnum.PROVIDER);
                            }}
                            className={classes.appBarLink}
                        >
                            {translate('imprint')}
                        </WbHeaderMetaLink>
                        {!isAdminPage && (
                            <WbHeaderMetaLink
                                className={classes.appBarLink}
                                slot="desktop-meta"
                                onClick={() => navigateTo(Routes.contact)}
                            >
                                {translate('contact')}
                            </WbHeaderMetaLink>
                        )}
                        <WbHeaderMetaLink>{translate('contact')}</WbHeaderMetaLink>
                        <WbHeaderLanguageMenu slot="desktop-meta" aria-label="Language Menu">
                            <nav aria-label="Language Navigation">
                                <a onClick={() => updateLangState(SupportedLanguages.de)}>
                                    <div className={classes.languageContainer}>
                                        <div
                                            className={
                                                langState === SupportedLanguages.de
                                                    ? classes.selectedLanguage
                                                    : classes.unselectedLanguage
                                            }
                                        >
                                            <span>{SupportedLanguages.de.toUpperCase()}</span>
                                        </div>
                                    </div>
                                </a>
                            </nav>
                            <nav aria-label="Language Navigation">
                                <a onClick={() => updateLangState(SupportedLanguages.en)}>
                                    <div className={classes.languageContainer}>
                                        <div
                                            className={
                                                langState === SupportedLanguages.en
                                                    ? classes.selectedLanguage
                                                    : classes.unselectedLanguage
                                            }
                                        >
                                            <span>{SupportedLanguages.en.toUpperCase()}</span>
                                        </div>
                                    </div>
                                </a>
                            </nav>
                        </WbHeaderLanguageMenu>

                        <WbHeaderButton slot="desktop-meta" onClick={(e) => handleMenuClick(e)}>
                            {userInfo?.userId ? (
                                <Badge badgeContent={<CheckOutlinedIcon className={classes.badge} fontSize="small" />}>
                                    <WbIcon name="bds/user/24"></WbIcon>
                                </Badge>
                            ) : (
                                <WbIcon name="bds/user/24"></WbIcon>
                            )}
                        </WbHeaderButton>
                        <LoginMenu
                            anchorElement={anchorElement}
                            setAnchorElement={setAnchorElement}
                            handleOpenChangeDevUserDialog={handleOpenChangeDevUserDialog}
                        />
                        {openChangeDevUserDialog && (
                            <ChangeDevUserDialog
                                open={openChangeDevUserDialog}
                                handleCloseChangeDevUserDialog={handleCloseChangeDevUserDialog}
                            />
                        )}

                        <WbHeaderLanguageMenu slot="tablet-meta-start" aria-label="Language Menu">
                            <nav aria-label="Language Navigation">
                                <a onClick={() => updateLangState(SupportedLanguages.de)}>
                                    <div className={classes.languageContainer}>
                                        <div
                                            className={
                                                langState === SupportedLanguages.de
                                                    ? classes.selectedLanguage
                                                    : classes.unselectedLanguage
                                            }
                                        >
                                            <span>{SupportedLanguages.de.toUpperCase()}</span>
                                        </div>
                                    </div>
                                </a>
                            </nav>
                            <nav aria-label="Language Navigation">
                                <a onClick={() => updateLangState(SupportedLanguages.en)}>
                                    <div className={classes.languageContainer}>
                                        <div
                                            className={
                                                langState === SupportedLanguages.en
                                                    ? classes.selectedLanguage
                                                    : classes.unselectedLanguage
                                            }
                                        >
                                            <span>{SupportedLanguages.en.toUpperCase()}</span>
                                        </div>
                                    </div>
                                </a>
                            </nav>
                        </WbHeaderLanguageMenu>

                        <WbHeaderButton slot="tablet-meta-end" onClick={(e) => handleMenuClick(e)}>
                            {userInfo?.userId ? (
                                <Badge badgeContent={<CheckOutlinedIcon className={classes.badge} fontSize="small" />}>
                                    <WbIcon name="bds/user/24"></WbIcon>
                                </Badge>
                            ) : (
                                <WbIcon name="bds/user/24"></WbIcon>
                            )}
                        </WbHeaderButton>
                        <LoginMenu
                            anchorElement={anchorElement}
                            setAnchorElement={setAnchorElement}
                            handleOpenChangeDevUserDialog={handleOpenChangeDevUserDialog}
                        />
                        <WbHeaderButton slot="mobile-meta-end" onClick={(e) => handleMenuClick(e)}>
                            {userInfo?.userId ? (
                                <Badge badgeContent={<CheckOutlinedIcon className={classes.badge} fontSize="small" />}>
                                    <WbIcon name="bds/user/24"></WbIcon>
                                </Badge>
                            ) : (
                                <WbIcon name="bds/user/24"></WbIcon>
                            )}
                        </WbHeaderButton>
                        <LoginMenu
                            anchorElement={anchorElement}
                            setAnchorElement={setAnchorElement}
                            handleOpenChangeDevUserDialog={handleOpenChangeDevUserDialog}
                        />
                    </>
                )}
            </WbHeaderBar>
            <Box sx={{ width: '100%', height: appbarSize }} />
            {showUserAppBarNavigation() && <MBAppBarUserNavigation />}
        </header>
    );
}
